import { OnboardingLayout } from "Components/Layout/OnboardingLayout";
import type { OnboardingStep } from "Components/Onboarding/BusinessEntity/constants";
import {
  STEP_TO_CONTENT_COMPONENT,
  type StepComponent,
  getTranslatedTitle,
} from "Components/Onboarding/BusinessEntity/utils";
import { useResource } from "Translations/Resources";
import { type ReactNode, useState } from "react";

type Props = {
  flow: ReadonlyArray<OnboardingStep>;
  // Resolved by the parent before mounting — the step is seeded once and then lives in client state only.
  initialStep: OnboardingStep;
  stepOverrides?: Partial<Record<OnboardingStep, StepComponent>>;
  leaveButton?: ReactNode;
};

export const OnboardingWizard = ({
  flow,
  initialStep,
  stepOverrides,
  leaveButton,
}: Props) => {
  const { t } = useResource();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(initialStep);

  const currentStepIndex = flow.indexOf(currentStep);
  const hasPreviousStep = currentStepIndex > 0;
  const hasNextStep = currentStepIndex < flow.length - 1;

  const goToNextStep = () => {
    if (hasNextStep) {
      setCurrentStep(flow[currentStepIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    if (hasPreviousStep) {
      setCurrentStep(flow[currentStepIndex - 1]);
    }
  };

  const CurrentStep =
    stepOverrides?.[currentStep] ?? STEP_TO_CONTENT_COMPONENT[currentStep];

  return (
    <OnboardingLayout
      title={getTranslatedTitle(currentStep, t)}
      onChevronClick={hasPreviousStep ? goToPrevStep : undefined}
      leaveButton={leaveButton}
    >
      <CurrentStep onNextStepRequested={goToNextStep} />
    </OnboardingLayout>
  );
};
